<script>
import Storage from "@/libs/Storage";
import constants from "@/libs/Constants";
import {getTooltipById} from "@/libs/Helpers";
import ModalWindow from "@/components/ModalWindow.vue";
import LangDropdown from "@/components/LangDropdown.vue";

export default {
  name: "ModalInformation",
  components: {
    LangDropdown,
    ModalWindow
  },
  emits: ['onModalAboutClose'],
  props: {
    propModalStatus: Boolean,
    propShowAboutStartup: Boolean,
    propShowSection: {type: String, default: null},
    propGlossaryTerm: {type: String, default: null},
  },
  watch: {
    showAboutStartup(newValue, oldValue) {
      //Keep value in local storage
      Storage.set(constants.STORAGE.SHOW_VISUALIZATION_ABOUT_STARTUP, newValue);
    },
    propShowSection(newValue, oldValue) {
      if ([constants.INFORMATION_OPTIONS.SUMMARY, constants.INFORMATION_OPTIONS.GLOSSARY].indexOf(newValue) !== -1)
        this.sectionVisible = newValue;
    },
    propGlossaryTerm(newValue, oldValue) {
      this.glossaryTerm = newValue;
      this.glossaryScrollToSelectedTerm();
    }
  },
  data() {
    return {
      constants,
      showAboutStartup: this.propShowAboutStartup,
      sectionVisible: 'summary',
      glossaryTerm: this.propGlossaryTerm
    }
  },
  methods: {
    onModalAboutShow() {
      const tooltipButtonAbout = getTooltipById('btnAbout');
      if (tooltipButtonAbout !== null)
        tooltipButtonAbout.hide();
    },
    onModalAboutClose() {
      this.$emit('onModalAboutClose')
    },
    onSectionClickChange(section) {
      this.glossaryTerm = null;
      this.sectionVisible = section;

      if (this.sectionVisible === constants.INFORMATION_OPTIONS.GLOSSARY)
        this.glossaryScrollToSelectedTerm();
    },
    glossaryScrollToSelectedTerm() {
      let elementDom = null;
      if (this.glossaryTerm !== null)
        elementDom = document.getElementById(`term_${this.glossaryTerm}`);

      //If no element found, use start of table as anchor
      if (elementDom === null)
        elementDom = document.getElementById('tableGlossary');

      if (elementDom !== undefined && elementDom !== null)
        setTimeout(() => {
          elementDom.scrollIntoView({behavior: 'smooth'});
        }, 300)
    },
    getGlossaryTerms() {
      let glossaryKeys = [
        "discharge",
        "discharge_anomaly",
        "temperature",
        "temperature_anomaly",
        "precipitation",
        "precipitation_anomaly",
        "actual_evapotranspiration",
        "actual_evapotranspiration_anomaly",
        "snow_water_equivalent",
        "soil_water_content",
        "percentile",
        "basin",
        "river_network",
        "derivation",
        "eave",
        "water_direction",
        "dam",
        "hydroelectric_plant"
      ];

      let glossaryTerms = [];
      glossaryKeys.forEach(key => {
        let prefix = `Glossary.${key}`;
        let name = this.$t(`${prefix}.name`);

        glossaryTerms.push({
          prefix: prefix,
          key: key,
          name: name,
        });
      });

      return glossaryTerms.sort((a, b) => {
        let a_key = a.name;
        let b_key = b.name;

        return a_key.localeCompare(b_key);
      });
    }
  }
}
</script>

<template>
  <ModalWindow prop-id="modalInformation"
               prop-size="xl"
               :prop-static="true"
               :prop-status="propModalStatus"
               :prop-is-open="showAboutStartup === true"
               :prop-disable-close-button-header="true"
               @on-show-modal="onModalAboutShow"
               @on-close-modal-click="onModalAboutClose">
    <template v-slot:title>
      <i class="bi bi-binoculars me-2"></i>
      <span>{{ $t('navbar.visualization') }}</span>
    </template>
    <template v-slot:subtitle>
      <span class="badge rounded-pill text-bg-purple mx-1">{{ $t('buttons.AboutVisualization') }}</span>
    </template>
    <template v-slot:next_to_title>
      <div class="dropdown">
        <LangDropdown
            prop-id="modalInformationLangDropdown"
            :prop-locale="this.$i18n.locale"
        />
      </div>
    </template>

    <div class="row border-bottom pb-2 mb-3">
      <div class="col">
        <div class="nav nav-pills border-0 justify-content-center ms-auto" id="v-pills-tab"
             role="tablist">
          <button class="nav-link"
                  :class="{'active': sectionVisible === constants.INFORMATION_OPTIONS.SUMMARY}"
                  id="v-pills-summary-tab" data-bs-toggle="pill"
                  data-bs-target="#v-pills-summary"
                  @click="onSectionClickChange(constants.INFORMATION_OPTIONS.SUMMARY)"
                  :aria-selected="sectionVisible === constants.INFORMATION_OPTIONS.SUMMARY ? 'true' : 'false'"
                  type="button" role="tab" aria-controls="v-pills-summary">
            <i class="bi bi-list-ul me-2"></i>
            <span>{{ $t('buttons.Summary') }}</span>
          </button>
          <button class="nav-link"
                  :class="{'active': sectionVisible === constants.INFORMATION_OPTIONS.GLOSSARY}"
                  id="v-pills-profile-tab" data-bs-toggle="pill" data-bs-target="#v-pills-profile"
                  type="button" role="tab" aria-controls="v-pills-profile"
                  :aria-selected="sectionVisible === constants.INFORMATION_OPTIONS.GLOSSARY ? 'true' : 'false'"
                  @click="onSectionClickChange(constants.INFORMATION_OPTIONS.GLOSSARY)">
            <i class="bi bi-journal-bookmark-fill me-2"></i>
            <span>{{ $t('buttons.Glossary') }}</span>
          </button>
        </div>
      </div>
    </div>
    <div class="tab-content mt-3" id="v-pills-tabContent">
      <!-- Main info -->
      <div class="tab-pane fade"
           :class="{'active show': sectionVisible === constants.INFORMATION_OPTIONS.SUMMARY}"
           id="v-pills-summary"
           role="tabpanel"
           aria-labelledby="v-pills-summary-tab"
           tabindex="0">
        <div class="row">
          <div class="col-12">
            <p v-html="$t('VisualizationInformation.general')" class="lh-sm"></p>
            <p v-html="$t('VisualizationInformation.sections')" class="lh-sm"></p>
          </div>
        </div>
        <div class="row my-3 mx-1">
          <div class="col-sm-12 col-lg-4 mb-sm-4 mb-lg-0">
            <div class="card h-100 border-0 shadow">
              <div class="card-header bg-primary text-white border-0 text-center">
                <h5 class="my-2">
                  <i class="bi bi-search pe-2"></i>
                  <span>{{ $t('SearchForm.monitoring') }}</span>
                </h5>
              </div>
              <div class="card-body">
                <div class="card-text mb-2" v-html="$t('VisualizationInformation.monitoring')"></div>
              </div>
            </div>
          </div>

          <div class="col-sm-12 col-lg-4 mb-sm-4 mb-lg-0">
            <div class="card h-100 border-0 shadow">
              <div class="card-header bg-primary text-white border-0 text-center">
                <h5 class="my-2">
                  <i class="bi bi-cloud-fog2 pe-2"></i>
                  <span>{{ $t('SearchForm.forecast') }}</span>
                </h5>
              </div>
              <div class="card-body">
                <div class="card-text mb-2" v-html="$t('VisualizationInformation.forecast')"></div>
              </div>
            </div>
          </div>

          <div class="col-sm-12 col-lg-4">
            <div class="card h-100 border-0 shadow">
              <div class="card-header bg-primary text-white border-0 text-center">
                <h5 class="my-2">
                  <i class="bi bi-graph-up pe-2"></i>
                  <span>{{ $t('SearchForm.projection') }}</span>
                </h5>
              </div>

              <div class="card-body">
                <div class="card-text mb-2" v-html="$t('VisualizationInformation.projection')"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Glossary -->
      <div class="tab-pane fade px-2" id="v-pills-profile"
           :class="{'active show': sectionVisible === constants.INFORMATION_OPTIONS.GLOSSARY}"
           role="tabpanel" aria-labelledby="v-pills-profile-tab"
           tabindex="0" style="max-height: 536px; overflow-y: auto; overflow-x: hidden;">

        <table class="table table-hover align-middle" id="tableGlossary">
          <tbody>
          <template v-for="item in getGlossaryTerms()">
            <tr>
              <td>
                <div class="row py-2" :id="`term_${item.key}`">
                  <div class="col-12"
                       :class="{
                    'text-success h5': item.key !== glossaryTerm,
                    'h4': item.key === glossaryTerm
                  }">
                    <span class="" :class="{
                    'badge rounded-pill text-bg-success': item.key === glossaryTerm
                  }">
                      {{ $t(`${item.prefix}.name`) }}
                    </span>
                  </div>
                  <div class="col-12" v-html="$t(`${item.prefix}.description`)"></div>
                </div>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
    </div>

    <template v-slot:footer>
      <div class="form-check form-switch pe-2">
        <input class="form-check-input"
               type="checkbox"
               role="switch"
               id="checkShowStartup"
               v-model="showAboutStartup"
               :disabled="false">
        <label class="form-check-label" for="checkShowStartup">
          {{ $t('InputSearchFormLabelTitle.showAlwaysStartup') }}
        </label>
      </div>
    </template>
  </ModalWindow>
</template>
